import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Inbox, Users, Tag, Share2, Heart, Search, 
  LayoutGrid, Table, Kanban, Settings, LogOut,
  Menu, X, ChevronRight, Plus, Folder
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';

const Sidebar = ({ onSearch }) => {
  const { user, logout } = useAuth();
  const { stats, clients } = useData();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onSearch?.(searchQuery);
    }
  };

  const navItems = [
    { 
      path: '/', 
      icon: Inbox, 
      label: 'Inbox', 
      badge: stats?.inbox_count,
      badgeColor: 'bg-amber-500'
    },
    { path: '/favorites', icon: Heart, label: 'Favorites', badge: stats?.favorites_count },
    { path: '/clients', icon: Users, label: 'Clients', badge: stats?.clients_count },
    { path: '/tags', icon: Tag, label: 'Tags', badge: stats?.tags_count },
    { path: '/collections', icon: Folder, label: 'Collections' },
    { path: '/shared', icon: Share2, label: 'Shared' },
  ];

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white tracking-tight">
          Ad<span className="text-violet-400">Vault</span>
        </h1>
        <p className="text-xs text-zinc-500 mt-1">Creative Inspiration</p>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            data-testid="sidebar-search"
            type="text"
            placeholder="Search ads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-800/50 border-zinc-700 text-zinc-200 placeholder:text-zinc-600"
          />
        </div>
      </form>

      {/* Navigation */}
      <nav className="space-y-1 flex-1">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={({ isActive }) => `
              flex items-center justify-between px-3 py-2.5 rounded-lg
              transition-colors duration-200
              ${isActive 
                ? 'bg-violet-500/10 text-violet-400 border-l-2 border-violet-500' 
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50'
              }
            `}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div className="flex items-center gap-3">
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </div>
            {item.badge !== undefined && item.badge > 0 && (
              <Badge 
                variant="secondary" 
                className={`${item.badgeColor || 'bg-zinc-700'} text-white text-xs px-2`}
              >
                {item.badge}
              </Badge>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Clients Quick Access */}
      {clients.length > 0 && (
        <div className="mt-6 pt-6 border-t border-zinc-800">
          <div className="flex items-center justify-between mb-3 px-3">
            <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
              Clients
            </span>
            <NavLink to="/clients">
              <Button variant="ghost" size="icon" className="h-6 w-6 text-zinc-500 hover:text-zinc-300">
                <Plus className="w-4 h-4" />
              </Button>
            </NavLink>
          </div>
          <div className="space-y-1">
            {clients.slice(0, 5).map((client) => (
              <NavLink
                key={client.id}
                to={`/?client=${client.id}`}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2 rounded-lg text-sm
                  ${isActive 
                    ? 'bg-zinc-800 text-zinc-200' 
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50'
                  }
                `}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: client.color }}
                />
                <span className="truncate">{client.name}</span>
                <span className="ml-auto text-xs text-zinc-600">{client.ad_count}</span>
              </NavLink>
            ))}
          </div>
        </div>
      )}

      {/* User section */}
      <div className="mt-auto pt-6 border-t border-zinc-800">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-9 h-9 rounded-full bg-violet-500/20 flex items-center justify-center">
            <span className="text-violet-400 font-medium">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-200 truncate">{user?.name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-zinc-500 hover:text-zinc-300"
            onClick={logout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 h-full w-64 bg-zinc-900/80 backdrop-blur-xl border-r border-white/5 p-6 flex-col z-40">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-zinc-900/90 backdrop-blur-xl border-b border-white/5 flex items-center justify-between px-4 z-50">
        <h1 className="text-xl font-bold text-white">
          Ad<span className="text-violet-400">Vault</span>
        </h1>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-zinc-400"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 bg-black/60 z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25 }}
            className="md:hidden fixed left-0 top-16 bottom-0 w-72 bg-zinc-900 border-r border-white/5 p-6 flex flex-col z-50 overflow-y-auto"
          >
            <SidebarContent />
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden mobile-nav fixed bottom-0 left-0 right-0 bg-zinc-900/90 backdrop-blur-xl border-t border-white/5 flex justify-around items-center py-2 z-50">
        <NavLink
          to="/"
          className={({ isActive }) => `
            flex flex-col items-center gap-1 p-2
            ${isActive ? 'text-violet-400' : 'text-zinc-500'}
          `}
        >
          <Inbox className="w-5 h-5" />
          <span className="text-xs">Inbox</span>
        </NavLink>
        <NavLink
          to="/favorites"
          className={({ isActive }) => `
            flex flex-col items-center gap-1 p-2
            ${isActive ? 'text-violet-400' : 'text-zinc-500'}
          `}
        >
          <Heart className="w-5 h-5" />
          <span className="text-xs">Favorites</span>
        </NavLink>
        <NavLink
          to="/clients"
          className={({ isActive }) => `
            flex flex-col items-center gap-1 p-2
            ${isActive ? 'text-violet-400' : 'text-zinc-500'}
          `}
        >
          <Users className="w-5 h-5" />
          <span className="text-xs">Clients</span>
        </NavLink>
        <NavLink
          to="/tags"
          className={({ isActive }) => `
            flex flex-col items-center gap-1 p-2
            ${isActive ? 'text-violet-400' : 'text-zinc-500'}
          `}
        >
          <Tag className="w-5 h-5" />
          <span className="text-xs">Tags</span>
        </NavLink>
      </nav>
    </>
  );
};

export default Sidebar;
